import { useFormikContext } from 'formik'
import { Typography } from '@mui/material'
import React, { useCallback, useMemo, useRef } from 'react'
import tw from 'twin.macro'
import { OTP_INPUT_LENGTH, OtpInputFormValues } from './OtpInputForm.interface'

interface Props {
  testIdPrefix?: string
}

const getDigitsFromCode = (code: string): string[] => {
  const codeDigits = code.split('').slice(0, OTP_INPUT_LENGTH)

  return Array.from({ length: OTP_INPUT_LENGTH }, (_, index) => {
    return codeDigits[index] || ''
  })
}

export const OtpInputFields = ({ testIdPrefix = 'sign-in-enter-code-code-input' }: Props) => {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([])

  const { values, errors, touched, setFieldValue } = useFormikContext<OtpInputFormValues>()

  const digits = useMemo(() => {
    return getDigitsFromCode(values.code)
  }, [values.code])

  const hasValidationError = Boolean(touched.code && errors.code)

  const updateCode = useCallback(
    async (newDigits: string[]) => {
      const newCode = newDigits.join('')

      await setFieldValue('code', newCode)
    },
    [setFieldValue],
  )

  const focusInput = useCallback((index: number) => {
    inputRefs.current[index]?.focus()
  }, [])

  const handleChange = useCallback(
    (index: number) => {
      return (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value.replace(/\D/g, '')

        if (!inputValue) {
          const newDigits = [...digits]
          newDigits[index] = ''
          updateCode(newDigits)

          return
        }

        const newDigits = [...digits]
        let currentIndex = index

        inputValue.split('').forEach((character) => {
          if (currentIndex >= OTP_INPUT_LENGTH) {
            return
          }

          newDigits[currentIndex] = character
          currentIndex += 1
        })

        updateCode(newDigits)

        const nextIndex = Math.min(currentIndex, OTP_INPUT_LENGTH - 1)
        focusInput(nextIndex)
      }
    },
    [digits, focusInput, updateCode],
  )

  const handleKeyDown = useCallback(
    (index: number) => {
      return (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== 'Backspace') {
          return
        }

        if (digits[index]) {
          return
        }

        if (index === 0) {
          return
        }

        event.preventDefault()

        const newDigits = [...digits]
        newDigits[index - 1] = ''
        updateCode(newDigits)
        focusInput(index - 1)
      }
    },
    [digits, focusInput, updateCode],
  )

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      event.preventDefault()

      const pastedDigits = event.clipboardData
        .getData('text')
        .replace(/\D/g, '')
        .slice(0, OTP_INPUT_LENGTH)
        .split('')

      if (!pastedDigits.length) {
        return
      }

      const newDigits = Array.from({ length: OTP_INPUT_LENGTH }, (_, index) => {
        return pastedDigits[index] || ''
      })

      updateCode(newDigits)

      const focusIndex = Math.min(pastedDigits.length, OTP_INPUT_LENGTH) - 1
      focusInput(Math.max(focusIndex, 0))
    },
    [focusInput, updateCode],
  )

  return (
    <div css={tw`mb-6`}>
      <div css={tw`flex justify-center gap-2`}>
        {digits.map((digit, index) => {
          return (
            <input
              key={index}
              ref={(element) => {
                inputRefs.current[index] = element
              }}
              autoFocus={index === 0}
              autoComplete={index === 0 ? 'one-time-code' : 'off'}
              css={[
                tw`h-12 w-12 appearance-none rounded-lg border border-solid bg-shades-0 text-center text-lg font-medium shadow-none outline-none`,
                hasValidationError ? tw`border-critical-500` : tw`border-shades-200`,
                tw`focus:border-primary-500`,
                { boxShadow: 'none' },
              ]}
              inputMode="numeric"
              type="text"
              value={digit}
              onChange={handleChange(index)}
              onKeyDown={handleKeyDown(index)}
              onPaste={handlePaste}
              data-testid={`${testIdPrefix}-${index}`}
            />
          )
        })}
      </div>
      {hasValidationError && (
        <Typography css={tw`mt-2 text-center text-critical-500`} variant="body2">
          {errors.code}
        </Typography>
      )}
    </div>
  )
}
